import { formatMoney } from '@bqcrm/shared';
import { ActionIcon, Button, Checkbox, Group, NumberInput, Popover, SegmentedControl, Stack, Text } from '@mantine/core';
import { IconCalculator } from '@tabler/icons-react';
import { useState } from 'react';

type Base = 'total' | 'rest';

/** Wyliczenie kwoty płatności: procent wartości umowy albo pozostałej kwoty (bez tej płatności), opcjonalnie do pełnych złotych. */
export function calcAmountGr(totalGr: number, othersGr: number, pct: number, base: Base, roundZl: boolean): number {
  const from = base === 'total' ? totalGr : Math.max(0, totalGr - othersGr);
  const gr = Math.round((from * pct) / 100);
  return roundZl ? Math.round(gr / 100) * 100 : gr;
}

/** Kalkulator przy kwocie płatności harmonogramu — bez zewnętrznego kalkulatora. */
export function AmountCalculator({ totalGr, othersGr, totalLabel, onApply }: { totalGr: number; othersGr: number; totalLabel: string; onApply: (gr: number) => void }) {
  const [opened, setOpened] = useState(false);
  const [pct, setPct] = useState<number | ''>('');
  const [base, setBase] = useState<Base>('total');
  const [roundZl, setRoundZl] = useState(false);
  const restGr = totalGr - othersGr;
  const result = pct === '' ? null : calcAmountGr(totalGr, othersGr, pct, base, roundZl);
  const apply = (gr: number) => {
    onApply(gr);
    setOpened(false);
  };

  return (
    <Popover opened={opened} onChange={setOpened} position="bottom-end" withArrow shadow="md" width={300} trapFocus>
      <Popover.Target>
        <ActionIcon variant="subtle" size="sm" onClick={() => setOpened((o) => !o)} aria-label="Kalkulator kwoty" title="Kalkulator kwoty">
          <IconCalculator size={14} />
        </ActionIcon>
      </Popover.Target>
      <Popover.Dropdown>
        <Stack gap="xs">
          <div>
            <Text size="xs" c="dimmed">
              {totalLabel}: <b>{formatMoney(totalGr)}</b>
            </Text>
            <Text size="xs" c="dimmed">
              Pozostało do rozplanowania (bez tej płatności): <b>{formatMoney(restGr)}</b>
            </Text>
          </div>
          <SegmentedControl
            size="xs"
            fullWidth
            value={base}
            onChange={(v) => setBase(v as Base)}
            data={[
              { value: 'total', label: 'z wartości' },
              { value: 'rest', label: 'z pozostałej' },
            ]}
          />
          <NumberInput size="xs" label="Procent" suffix=" %" min={0} max={100} decimalScale={2} decimalSeparator="," value={pct} onChange={(v) => setPct(v === '' ? '' : Number(v))} data-autofocus />
          <Group gap={4}>
            {[10, 20, 30, 40, 50, 100].map((p) => (
              <Button key={p} size="compact-xs" variant={pct === p ? 'filled' : 'default'} onClick={() => setPct(p)}>
                {p}%
              </Button>
            ))}
          </Group>
          <Checkbox size="xs" label="Zaokrąglij do pełnych złotych" checked={roundZl} onChange={(e) => setRoundZl(e.currentTarget.checked)} />
          <Text size="sm">
            Wynik: <b>{result === null ? '—' : formatMoney(result)}</b>
          </Text>
          <Group gap="xs" justify="space-between">
            <Button size="xs" variant="default" disabled={restGr <= 0} onClick={() => apply(restGr)}>
              Wstaw resztę
            </Button>
            <Button size="xs" disabled={result === null} onClick={() => result !== null && apply(result)}>
              Wstaw kwotę
            </Button>
          </Group>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
}
